import { StatsTableColumn } from '../enum/TableColumn';
import { CURRENCY_BORDER_COLOR } from '../constant/constants';
import { GeoColumns } from '../enum/GeoDivisionColumnName';
import { t } from 'i18next';

export function isGeoColumnExcludingLocation(column: string): boolean {
  const geoValues = Object.values(GeoColumns).filter((c) => c !== GeoColumns.LOCATION);

  return geoValues.includes(column as any);
}

export function isGeoColumn(column: string): boolean {
  const isGeo = Object.values(GeoColumns).includes(column as GeoColumns);

  return isGeo;
}

/**
 * Returns the appropriate CSS class for a given table column
 * depending on the rendering context.
 */
export function getTableClass(
  column: string,
  context: 'column' | 'footer' | 'footer_row',
  isViewPerCurrency?: boolean,
): string {
  const isCurrency = column === StatsTableColumn.CCY;
  const isLocation = column === GeoColumns.LOCATION;

  switch (context) {
    case 'column':
      return getColumnClass(column, isCurrency, isLocation);

    case 'footer':
      return getFooterClass(column, isCurrency, isLocation, isViewPerCurrency);

    case 'footer_row':
      return getFooterRowClass(isCurrency, isLocation);

    default:
      return '';
  }
}

/* -------------------------------------------------------------------------- */
/*                               Helper Methods                               */
/* -------------------------------------------------------------------------- */

/**
 * Returns class names for regular table columns.
 */
function getColumnClass(column: string, isCurrency: boolean, isLocation: boolean): string {
  const baseClass = isGeoColumn(column) || isCurrency ? 'sticky_column' : 'column';
  const modifier = isLocation ? 'location' : isCurrency ? 'currency' : '';
  return joinClasses(baseClass, modifier);
}

/**
 * Returns class names for footer cells.
 */
function getFooterClass(column: string, isCurrency: boolean, isLocation: boolean, isViewPerCurrency?: boolean): string {
  if (isViewPerCurrency) return 'sum_column';
  if (isGeoColumnExcludingLocation(column)) return 'empty_columns';
  if (isLocation || isCurrency) return 'button_column';
  return 'sum_column';
}

/**
 * Returns class names for footer rows.
 */
function getFooterRowClass(isCurrency: boolean, isLocation: boolean): string {
  if (isLocation) return 'location';
  if (isCurrency) return 'currency';
  return '';
}

/**
 * Joins multiple CSS class names safely.
 */
function joinClasses(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

export const borderStyle = (params: {
  idx?: number; // Optional row index
  dataLength?: number; // Total rows count
  isFull?: boolean; // Always draw top & bottom borders
  isLastColumn?: boolean; // Draw right border
  isCCYColumn?: boolean; // Draw left border for CCY
  isFirstColumnAfterCCY?: boolean; // Draw left border for first column after CCY
}): React.CSSProperties => {
  const {
    idx,
    dataLength,
    isFull = false,
    isLastColumn = false,
    isCCYColumn = false,
    isFirstColumnAfterCCY = false,
  } = params;
  const topBorder = isFull || idx === 0 ? `1px solid ${CURRENCY_BORDER_COLOR}` : undefined;

  const bottomBorder =
    isFull || (typeof idx === 'number' && typeof dataLength === 'number' && idx === dataLength - 1)
      ? `1px solid ${CURRENCY_BORDER_COLOR}`
      : undefined;

  return {
    borderTop: topBorder,
    borderRight: isLastColumn ? `1px solid ${CURRENCY_BORDER_COLOR}` : undefined,
    borderBottom: bottomBorder,
    borderLeft: isCCYColumn || isFirstColumnAfterCCY ? `1px solid ${CURRENCY_BORDER_COLOR}` : undefined,
  };
};

export function getCellClass(column: string, row: Record<string, number>): string {
  const isCurrency = column === StatsTableColumn.CCY;
  const isNumber = typeof row[column] === 'number';
  const actionColumn = column === t('cashRegister.table.header.actions');

  if (isCurrency || isNumber) {
    return 'text_right';
  } else if (actionColumn) {
    return 'image_column';
  } else return 'entity_name';
}
